import { useAccountsStore, type AccountType } from '@/entities/account';
import { useSessionStore } from '@/entities/session';
import { useTransferInternalSettingsDictionary } from '@/shared/api';
import { useCallback } from 'react';

const useAssetTransferAvailability = () => {
  const { session } = useSessionStore();
  const isVerified = session?.additional?.isVerified || false;
  const { spot, invest, leverage, ewallet } = useAccountsStore();
  const { transferInternalSettingsData } =
    useTransferInternalSettingsDictionary();

  const getAccountTypeTransferAvailability = useCallback(
    (accountType: AccountType) => {
      if (accountType === 'ewallet') {
        return getEwalletAccountTypeTransferAvailability();
      }

      if (!transferInternalSettingsData) {
        return false;
      }

      const transferInternalSettings =
        transferInternalSettingsData.value[accountType];

      return (
        transferInternalSettings?.isInternalDepositAllowed ||
        transferInternalSettings?.isInternalWithdrawAllowed
      );
    },
    [transferInternalSettingsData]
  );

  const getEwalletAccountTypeTransferAvailability = useCallback(() => {
    if (!transferInternalSettingsData) {
      return false;
    }

    const { isInternalDepositAllowed, isInternalWithdrawAllowed } =
      transferInternalSettingsData.value.leverage;

    const onlyLeverageIsAvailable =
      spot?.accounts.length === 0 &&
      invest?.accounts.length === 0 &&
      !!leverage &&
      leverage?.accounts.length > 0;

    const ewalletIsEmpty = ewallet?.accounts.length === 0;

    const isLaverageTrasferBlocked =
      onlyLeverageIsAvailable &&
      ewalletIsEmpty &&
      leverage.balance > 0 &&
      isInternalDepositAllowed === true &&
      isInternalWithdrawAllowed === false;

    return !isLaverageTrasferBlocked;
  }, [
    isVerified,
    ewallet,
    leverage,
    spot,
    invest,
    transferInternalSettingsData,
  ]);

  return {
    getAccountTypeTransferAvailability,
  };
};

export { useAssetTransferAvailability };
